import { type FC, memo, type ReactNode } from 'react';

import type { ContractVersionInsuranceEntity, ContractVersionProgram } from '@/types/contract';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Pill,
  Typography
} from '@/ui';
import { phaseTypeAbbreviation } from '@/utils/contractLabels';

import './styles.css';
import type { Props } from './types';

const labelInsuranceEntity = (entity: ContractVersionInsuranceEntity) =>
  `${entity.insurance_provider_name} · ${entity.insurance_type} · ${entity.jurisdiction_abbreviation}`;
const programLine = (program: ContractVersionProgram) =>
  `${program.program_name} · ${phaseTypeAbbreviation(program.phase_type)}${program.is_dual ? ' · Dual' : ''}`;

const section = (title: string, count: number, children: ReactNode) => (
  <section className="min-w-0">
    <Typography variant="label" as="h3" muted className="flex items-center gap-2">
      {title}
      <Pill size="sm" variant="subtle" color="neutral">
        {count}
      </Pill>
    </Typography>
    <div className="mt-2 max-h-80 space-y-2 overflow-y-auto">{children}</div>
  </section>
);

const ContractVersionChanges: FC<Props> = ({ version }) => {
  const previous = version.version - 1;
  const programChanges = version.changed.filter(
    (change) => change.added_programs.length + change.removed_programs.length > 0
  );
  const hasChanges = version.added.length + version.removed.length + programChanges.length > 0;

  return (
    <Accordion>
      <AccordionItem className="bg-white dark:bg-uniform-gray-900">
        <Typography variant="heading" as="h2">
          <AccordionTrigger>
            Changes in version {version.version}
            <Typography as="span" muted weight="normal" className="block">
              Compared with version {previous}.
            </Typography>
          </AccordionTrigger>
        </Typography>
        <AccordionContent>
          {hasChanges ? (
            <div className="grid gap-6 lg:grid-cols-3">
              {version.added.length > 0 &&
                section(
                  'Added entities',
                  version.added.length,
                  <>
                    {version.added.map((entity) => (
                      <Typography key={entity.id}>+ {labelInsuranceEntity(entity)}</Typography>
                    ))}
                  </>
                )}
              {version.removed.length > 0 &&
                section(
                  'Removed entities',
                  version.removed.length,
                  <>
                    {version.removed.map((entity) => (
                      <Typography key={entity.id}>− {labelInsuranceEntity(entity)}</Typography>
                    ))}
                  </>
                )}
              {programChanges.length > 0 &&
                section(
                  'Program changes',
                  programChanges.length,
                  <>
                    {programChanges.map((change) => (
                      <div key={change.insurance_entity.id}>
                        <Typography weight="medium">
                          {labelInsuranceEntity(change.insurance_entity)}
                        </Typography>
                        <ul className="mt-1 space-y-1 pl-2">
                          {change.added_programs.map((program) => (
                            <Typography as="li" key={program.program_id}>
                              + {programLine(program)}
                            </Typography>
                          ))}
                          {change.removed_programs.map((program) => (
                            <Typography as="li" key={program.program_id}>
                              − {programLine(program)}
                            </Typography>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </>
                )}
            </div>
          ) : (
            <Typography muted>No changes from version {previous}.</Typography>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default memo(ContractVersionChanges);
